import { useRef } from "react";
import { Calendar } from "lucide-react";
import { toLocalInput } from "@/lib/timefmt";

export default function LocalDateTimeField({ label, value, onChange, testId, disableFuture = false }) {
  const [datePart, timePart] = (value || "").split("T");
  const hh = timePart ? timePart.slice(0, 2) : "";
  const mm = timePart ? timePart.slice(3, 5) : "";
  const now = new Date();
  const p = (n) => String(n).padStart(2, "0");
  const todayStr = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
  const isToday = datePart === todayStr;
  const nowH = now.getHours();
  const nowM = now.getMinutes();

  const emit = (d, h, m) => {
    if (!d) { onChange(""); return; }
    let out = `${d}T${h || "00"}:${m || "00"}`;
    if (disableFuture && new Date(out) > now) out = toLocalInput(now.toISOString());
    onChange(out);
  };

  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
  const minutes = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));
  const dateRef = useRef(null);
  const cls = "px-2 py-2 rounded-md bg-slate-900 border border-slate-800 text-sm focus:border-blue-500 outline-none";
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">{label} (local, 24h)</label>
      <div className="flex gap-1.5">
        <div className="relative flex-1 min-w-[120px]">
          <input ref={dateRef} type="date" value={datePart || ""} max={disableFuture ? todayStr : undefined}
            onChange={(e) => emit(e.target.value, hh, mm)}
            data-testid={`${testId}-date`}
            className={`${cls} w-full pr-8 [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:opacity-0`} />
          <button type="button" tabIndex={-1}
            onClick={() => {
              const el = dateRef.current;
              if (!el) return;
              try {
                if (el.showPicker) el.showPicker();
                else el.focus();
              } catch {
                el.focus();
              }
            }}
            data-testid={`${testId}-date-cal`} title="Open calendar"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors">
            <Calendar className="h-4 w-4" />
          </button>
        </div>
        <select value={hh} onChange={(e) => emit(datePart, e.target.value, mm)} data-testid={`${testId}-hour`} className={`${cls} w-14 px-1`}>
          <option value="">hh</option>
          {hours.map((h) => (
            <option key={h} value={h} disabled={disableFuture && isToday && Number(h) > nowH}
              className={disableFuture && isToday && Number(h) > nowH ? "text-slate-600" : ""}>{h}</option>
          ))}
        </select>
        <select value={mm} onChange={(e) => emit(datePart, hh, e.target.value)} data-testid={`${testId}-minute`} className={`${cls} w-14 px-1`}>
          <option value="">mm</option>
          {minutes.map((m) => (
            <option key={m} value={m}
              disabled={disableFuture && isToday && Number(hh) === nowH && Number(m) > nowM}
              className={disableFuture && isToday && Number(hh) === nowH && Number(m) > nowM ? "text-slate-600" : ""}>{m}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
